import React, { useContext, useState } from 'react';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadRequestOption } from '@rc-component/upload/lib/interface';
import { Button, Modal, notification, Table, Tag, Upload } from 'antd';

import ProgramDashboardContext from '@totem/components/services/optimize/v2/dashboard/ProgramDashboardContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ImportSheetResult {
  sheet: string;
  created: number;
  updated: number;
  skipped: number;
  errors?: string[];
}

interface OptimizeImportResult {
  sheets: ImportSheetResult[];
}

// ---------------------------------------------------------------------------
// Result modal columns
// ---------------------------------------------------------------------------

const RESULT_COLUMNS = [
  {
    title: 'Sheet',
    dataIndex: 'sheet',
    key: 'sheet',
    render: (val: string) => <strong>{val}</strong>,
  },
  {
    title: 'Created',
    dataIndex: 'created',
    key: 'created',
    align: 'center' as const,
    render: (val: number) =>
      val > 0 ? <Tag color="green">{val}</Tag> : <span>—</span>,
  },
  {
    title: 'Updated',
    dataIndex: 'updated',
    key: 'updated',
    align: 'center' as const,
    render: (val: number) =>
      val > 0 ? <Tag color="blue">{val}</Tag> : <span>—</span>,
  },
  {
    title: 'Skipped',
    dataIndex: 'skipped',
    key: 'skipped',
    align: 'center' as const,
    render: (val: number) =>
      val > 0 ? <Tag color="default">{val}</Tag> : <span>—</span>,
  },
  {
    title: 'Status',
    key: 'status',
    render: (_: unknown, row: ImportSheetResult) => {
      if (row.errors && row.errors.length > 0) {
        return <Tag color="red">{row.errors.length} error(s)</Tag>;
      }
      if (row.created === 0 && row.updated === 0 && row.skipped === 0) {
        return <Tag color="warning">No data</Tag>;
      }
      return <Tag color="success">OK</Tag>;
    },
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ProgramImportButton = () => {
  const { buildingId, programId, requestRefresh } = useContext(
    ProgramDashboardContext,
  );
  const { getApiToken } = useContext(UserProfileContext);

  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState<OptimizeImportResult | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleUpload = ({ file, onSuccess, onError }: UploadRequestOption) => {
    setImporting(true);

    const form = new FormData();
    form.append('file', file as File);

    form.append('programId', programId);

    fetch(`${BUILDINGS_ENDPOINT}/${buildingId}/optimize/import`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: form,
    })
      .then((res) => {
        if (!res.ok) {
          return res.text().then((msg) => {
            throw new Error(msg || `Server error ${res.status}`);
          });
        }
        return res.json() as Promise<OptimizeImportResult>;
      })
      .then((data) => {
        setResult(data);

        setModalOpen(true);

        requestRefresh();
        if (typeof onSuccess === 'function') {
          onSuccess(data);
        }
      })
      .catch((err: Error) => {
        notification.error({
          message: 'Import failed',
          description: err.message,
          duration: 0,
        });
        if (typeof onError === 'function') {
          onError(err);
        }
      })
      .finally(() => setImporting(false));
  };

  const handleDownloadTemplate = () => {
    if (!buildingId) return;
    setDownloading(true);

    fetch(`${BUILDINGS_ENDPOINT}/${buildingId}/optimize/import/template`, {
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Server error ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        link.download = 'Optimize_Import_Template.xlsx';

        link.click();

        URL.revokeObjectURL(url);
      })
      .catch((err: Error) => {
        notification.error({
          message: 'Download failed',
          description: err.message,
        });
      })
      .finally(() => setDownloading(false));
  };

  const hasErrors =
    result?.sheets.some((sht) => sht.errors && sht.errors.length > 0) ?? false;

  const expandedRowRender = (row: ImportSheetResult) => {
    if (!row.errors || row.errors.length === 0) {
      return null;
    }
    return (
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {row.errors.map((err, idx) => (
          <li key={idx} style={{ color: 'var(--ant-color-error)' }}>
            {err}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <Button
        icon={<DownloadOutlined />}
        loading={downloading}
        disabled={!buildingId}
        onClick={handleDownloadTemplate}
      >
        Download Template
      </Button>

      <Upload
        accept=".xlsx"
        showUploadList={false}
        customRequest={handleUpload}
      >
        <Button
          icon={<UploadOutlined />}
          loading={importing}
          disabled={!buildingId || !programId}
        >
          Import Workbook
        </Button>
      </Upload>

      <Modal
        title="Import Results"
        open={modalOpen}
        onOk={() => setModalOpen(false)}
        onCancel={() => setModalOpen(false)}
        cancelButtonProps={{ style: { display: 'none' } }}
        width={640}
      >
        {result && (
          <Table
            dataSource={result.sheets}
            columns={RESULT_COLUMNS}
            rowKey="sheet"
            pagination={false}
            size="small"
            expandable={
              hasErrors
                ? {
                    expandedRowRender,
                    rowExpandable: (row) =>
                      !!(row.errors && row.errors.length > 0),
                  }
                : undefined
            }
          />
        )}
      </Modal>
    </>
  );
};

export default ProgramImportButton;
